import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faBoxArchive, faDownload } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { useConti } from '../../components/conti/ContiLayout';
import { ICONE, ICONE_VOCI, IconaTonda } from '../../components/conti/contiTheme';
import { creaVoce, getPiano, importaVociDaFirefly, messaggioErrore, modificaVoce } from '../../services/contiService';

const COLORI = ['#e03131', '#e8590c', '#f08c00', '#2f9e44', '#0ca678', '#1c7ed6', '#3b5bdb', '#7048e8', '#c2255c', '#495057'];

const PianoConti = () => {
  const { scope, tema } = useConti();
  const [voci, setVoci] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [form, setForm] = useState(null);
  const [salvataggio, setSalvataggio] = useState(false);
  const [mostraArchiviate, setMostraArchiviate] = useState(false);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      setVoci(await getPiano(scope));
    } catch (e) {
      toast.error(messaggioErrore(e, 'Impossibile caricare il piano dei conti'));
    } finally {
      setCaricamento(false);
    }
  }, [scope]);

  useEffect(() => { carica(); }, [carica]);

  const salva = async (e) => {
    e.preventDefault();
    setSalvataggio(true);
    const payload = { nome: form.nome, tipo: form.tipo, icona: form.icona || null, colore: form.colore || null };
    try {
      if (form.id) {
        await modificaVoce(form.id, payload);
        toast.success('Voce aggiornata');
      } else {
        await creaVoce({ ...payload, scope });
        toast.success('Voce creata e sincronizzata con Firefly');
      }
      setForm(null);
      carica();
    } catch (err) {
      toast.error(messaggioErrore(err, 'Salvataggio non riuscito'));
    } finally {
      setSalvataggio(false);
    }
  };

  const archivia = async (v) => {
    const msg = v.attivo ? `Archiviare "${v.nome}"? Non sarà più proponibile, lo storico resta.` : `Riattivare "${v.nome}"?`;
    if (!window.confirm(msg)) return;
    try {
      await modificaVoce(v.id, { attivo: !v.attivo });
      carica();
    } catch (err) {
      toast.error(messaggioErrore(err));
    }
  };

  const importa = async () => {
    if (!window.confirm('Creo le voci partendo dai conti di spesa e di entrata già presenti in Firefly. Procedo?')) return;
    try {
      const r = await importaVociDaFirefly(scope);
      toast.success(r.nuove ? `${r.nuove} voci importate` : 'Nessuna voce nuova da importare');
      carica();
    } catch (err) {
      toast.error(messaggioErrore(err, 'Importazione non riuscita'));
    }
  };

  const colonna = (tipo, titolo) => {
    const elenco = voci.filter((v) => v.tipo === tipo && (mostraArchiviate || v.attivo));
    return (
      <Col lg={6}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0" style={{ color: tema.scuro }}>{titolo} <span className="text-muted" style={{ fontSize: 14 }}>({elenco.length})</span></h5>
          <Button size="sm" variant="outline-primary" onClick={() => setForm({ nome: '', tipo, icona: 'tag', colore: tema.colore })}>
            <FontAwesomeIcon icon={faPlus} className="me-1" />Aggiungi
          </Button>
        </div>
        <div className="d-grid gap-2">
          {elenco.map((v) => (
            <div key={v.id} className="d-flex align-items-center gap-3" style={{
              background: '#fff', borderRadius: 12, padding: '10px 14px', boxShadow: '0 1px 6px rgba(0,0,0,.06)',
              opacity: v.attivo ? 1 : 0.5, borderLeft: `4px solid ${v.colore || tema.colore}`,
            }}>
              <IconaTonda icona={v.icona} colore={v.colore || tema.colore} size={38} />
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }} className="text-truncate">{v.nome}</div>
                <div className="text-muted" style={{ fontSize: 12 }}>{v.movimenti} moviment{v.movimenti === 1 ? 'o' : 'i'}{!v.attivo ? ' · archiviata' : ''}</div>
              </div>
              <Button size="sm" variant="light" onClick={() => setForm({ id: v.id, nome: v.nome, tipo: v.tipo, icona: v.icona || 'tag', colore: v.colore || tema.colore })}>
                <FontAwesomeIcon icon={faPen} />
              </Button>
              <Button size="sm" variant="light" onClick={() => archivia(v)} title={v.attivo ? 'Archivia' : 'Riattiva'}>
                <FontAwesomeIcon icon={faBoxArchive} />
              </Button>
            </div>
          ))}
          {elenco.length === 0 && <div className="text-muted text-center py-4" style={{ border: '2px dashed #ced4da', borderRadius: 12 }}>Nessuna voce</div>}
        </div>
      </Col>
    );
  };

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="text-muted">
          Le voci sono le categorie di Firefly di <strong>{tema.nome}</strong>: ogni modifica si riflette lì.
        </div>
        <div className="d-flex align-items-center gap-2">
          <Form.Check type="switch" id="mostra-arch-voci" label="Mostra archiviate" checked={mostraArchiviate}
            onChange={(e) => setMostraArchiviate(e.target.checked)} />
          <Button variant="outline-secondary" onClick={importa}><FontAwesomeIcon icon={faDownload} className="me-2" />Importa da Firefly</Button>
        </div>
      </div>
      {caricamento ? <div className="text-center py-5"><Spinner animation="border" /></div> : (
        <Row className="g-4">{colonna('spesa', 'Spese')}{colonna('entrata', 'Entrate')}</Row>
      )}

      <Modal show={!!form} onHide={() => setForm(null)} centered>
        {form && (
          <Form onSubmit={salva}>
            <Modal.Header closeButton style={{ background: tema.morbido }}>
              <Modal.Title style={{ color: tema.scuro }}>{form.id ? 'Modifica voce' : 'Nuova voce'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Nome</Form.Label>
                <Form.Control required autoFocus value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Tipo</Form.Label>
                <Form.Select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                  <option value="spesa">Spesa</option>
                  <option value="entrata">Entrata</option>
                </Form.Select>
              </Form.Group>
              <Form.Label>Colore</Form.Label>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {COLORI.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, colore: c })}
                    style={{ width: 30, height: 30, borderRadius: '50%', background: c, cursor: 'pointer',
                      border: form.colore === c ? '3px solid #212529' : '2px solid #fff', boxShadow: '0 0 0 1px #ced4da' }} />
                ))}
              </div>
              <Form.Label>Icona</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {ICONE_VOCI.map((k) => (
                  <button key={k} type="button" onClick={() => setForm({ ...form, icona: k })}
                    style={{ width: 40, height: 40, borderRadius: 10, cursor: 'pointer', background: form.icona === k ? tema.morbido : '#fff',
                      border: form.icona === k ? `2px solid ${tema.colore}` : '1px solid #ced4da' }}>
                    <FontAwesomeIcon icon={ICONE[k]} />
                  </button>
                ))}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setForm(null)}>Annulla</Button>
              <Button type="submit" disabled={salvataggio} style={{ background: tema.colore, borderColor: tema.colore }}>
                {salvataggio ? 'Salvo…' : 'Salva'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default PianoConti;
